import { Role } from "@/api/enums/Role";
import { ExamAnswerDto } from "@/api/live-exam/dto/exam-answer.dto";
import {
  ExamDtoClient,
  ExamQuestionStateDtoClient,
} from "@/api/live-exam/dto/exam.dto";
import { Badge } from "@/components/atoms/Badge";
import { ScrollArea } from "@/components/atoms/ScrollArea";
import { Skeleton } from "@/components/atoms/Skeleton";
import {
  ColumnPerimeter,
  ColumnPerimeterUtil,
} from "@/constants/column-perimeter.enum";
import { socket } from "@/hooks/socket";
import usePersistent from "@/hooks/use-persistent";
import { useSocketStore } from "@/hooks/useSocketStore";
import { Perimeter } from "@/lib/perimeter";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ExamContext } from "../exam.context";
import ProgressBar from "./ProgressBar";
import QuestionCard from "./QuestionCard";
import { QuestionsTab, tabFilter } from "./questions-tab.enum";
import QuestionTabs from "./QuestionTabs";
import TestBanner from "./TestBanner";
import TopBar from "./TopBar/TopBar";
import { useCopyDetection } from "@/hooks/useCopyDetection";
import { toast } from "sonner";

const cookiesErrorMessage =
  "Les cookies nécessaires n'ont pas été autorisés par votre navigateur. " +
  "Veuillez autoriser les cookies pour *.cifmd-inscription.fr dans les paramètres de votre navigateur, " +
  "désactiver la protection renforcée contre le pistage/les bloqueurs de confidentialité, " +
  "ou essayer un autre navigateur (Chrome/Edge en mode normal).";

// This component sorts questions by column perimeter,
// then by question id.
//
// This is important : admins
// want to know the order of questions
// the candidate was presented with.

type QuestionsSection = {
  [perimeter: string]: {
    index: number;
    question: ExamQuestionStateDtoClient;
  }[];
};

type Scrolls = Record<string, number>;

const Questions = () => {
  const [exam, setExam] = useState<ExamDtoClient | undefined>(undefined);
  const [perimeter, setPerimeter] = usePersistent<string>(
    "exam-questions-perimeter",
    ""
  );
  const [tab, setTab] = useState<QuestionsTab>(QuestionsTab.All);
  const [waitingQuestions, setWaitingQuestions] = usePersistent<number[]>(
    "exam-waiting-questions",
    []
  );
  // to jump to a question
  const questionRefs = useRef<{ [questionId: number]: HTMLDivElement }>({});
  const { connected, namespace } = useSocketStore();
  const [displayWaiting, setDisplayWaiting] = usePersistent(
    "display-waiting",
    false
  );
  // to keep scroll position between tabs
  const [scrolls, setScrolls] = useState<Scrolls>({});
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const { isTest } = useOutletContext<ExamContext>();

  const [candidateInfo, setCandidateInfo] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    zendeskJwt?: string;
  } | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // question id -> number
  const questionsOrder = useMemo(() => {
    if (!exam) return undefined;
    const ordered = ColumnPerimeterUtil.sortQuestions(
      exam.map(({ question }) => ({
        id: question.id,
        perimeter: Perimeter.toCode(question.perimeter),
      }))
    );

    const d: Record<number, number> = {};
    for (let i = 0; i < ordered.length; i++) d[ordered[i].id] = i;

    return d;
  }, [exam]);

  // handle connection errors
  useEffect(() => {
    const displayConnectionError = (message: string) => {
      setConnectionError(message);
      toast.error("Erreur de connexion", {
        description: message,
        duration: 10000,
      });
    };

    const handleConnectionRefused = (reason: string) => {
      displayConnectionError(reason);
    };

    const handleConnectError = (error: any) => {
      const errorMessage = error?.message || String(error);
      if (errorMessage.includes("cookies") || errorMessage.includes("cookie")) {
        displayConnectionError(cookiesErrorMessage);
      }
    };

    socket.on("connectionRefused", handleConnectionRefused);
    socket.on("connect_error", handleConnectError);

    return () => {
      socket.off("connectionRefused", handleConnectionRefused);
      socket.off("connect_error", handleConnectError);
    };
  }, []);

  // reconnect if necessary
  useEffect(() => {
    if (!connected && !connectionError) {
      socket.io.opts.query = { role: Role.CANDIDATE };
      socket.connect();
    }
  }, [connected, namespace, connectionError]);

  // query exam
  useEffect(() => {
    if (connected) {
      socket.emit("getExam", {}, (data) => {
        setExam(data);
      });
      if (!isTest)
        socket.emit("getCandidateInfo", {}, (data) => {
          setCandidateInfo(data);
        });
    }
  }, [connected, isTest]);

  useCopyDetection();

  const orderedPerimeters = useMemo(
    () =>
      ColumnPerimeterUtil.sorted().map((p) =>
        ColumnPerimeterUtil.toNormalCode(p as ColumnPerimeter)
      ),
    []
  );

  const firstPerimeterInExam = useMemo(() => {
    if (!exam?.length) return undefined;
    return orderedPerimeters.find((code) =>
      exam.some(({ question }) => Perimeter.toCode(question.perimeter) === code)
    );
  }, [exam, orderedPerimeters]);

  const currentlyOpenPerimeter = useMemo(() => {
    const savedIsPresent =
      !!perimeter &&
      (exam ?? []).some(
        ({ question }) => Perimeter.toCode(question.perimeter) === perimeter
      );
    return savedIsPresent ? perimeter : firstPerimeterInExam ?? "";
  }, [exam, perimeter, firstPerimeterInExam]);

  // compute displayed sections
  const questions = useMemo(
    () =>
      (exam ?? []).filter(({ question }) => {
        if (displayWaiting) return waitingQuestions.includes(question.id);
        else
          return (
            Perimeter.toCode(question.perimeter) === currentlyOpenPerimeter
          );
      }),
    [exam, currentlyOpenPerimeter, displayWaiting, waitingQuestions]
  );

  const sections: QuestionsSection = useMemo(() => {
    const d: QuestionsSection = {};

    for (const question of questions) {
      const perimeter = Perimeter.toCode(question.question.perimeter);
      if (!d[perimeter]) d[perimeter] = [];
      d[perimeter].push({
        index: questionsOrder?.[question.question.id] ?? 0,
        question,
      });
    }

    return d;
  }, [questions, questionsOrder]);

  const jumpToQuestion = (questionId: number) => {
    // find question
    const question = questions.find(
      ({ question }) => question.id === questionId
    );

    if (!question) return;

    // change tab if the question is not visible
    for (const newTab of [tab, ...Object.values(QuestionsTab)]) {
      if (!tabFilter(question, newTab)) continue;
      setTab(newTab);
      break;
    }

    questionRefs.current[questionId]?.scrollIntoView({ behavior: "smooth" });
  };

  const onAnswerAcknowledged = (data: ExamAnswerDto) =>
    setExam((prev) =>
      prev?.map((question) => {
        if (question.question.id === data.questionId)
          question.answerOrder = data.answerOrder;

        return question;
      })
    );

  const setWaiting = (questionId: number, waiting: boolean) => {
    if (waiting) setWaitingQuestions([...waitingQuestions, questionId]);
    else
      setWaitingQuestions(waitingQuestions.filter((qId) => qId !== questionId));
  };

  const remainingQuestions = useMemo(
    () =>
      exam?.filter(({ answerOrder }) => answerOrder === undefined).length ?? 0,
    [exam]
  );

  const candidateName = useMemo(() => {
    if (!candidateInfo || !candidateInfo.firstName || !candidateInfo.lastName)
      return undefined;
    return `${candidateInfo.firstName} ${candidateInfo.lastName}`;
  }, [candidateInfo]);

  const retryConnection = useCallback(() => {
    setConnectionError(null);
    socket.io.opts.query = { role: Role.CANDIDATE };
    socket.connect();
  }, []);

  if (!connected) {
    // Show error message if connection failed
    if (connectionError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen w-full p-8 bg-white gap-4">
          <div className="max-w-2xl w-full bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              Erreur de connexion au serveur d'examen
            </h2>
            <p className="text-red-700 whitespace-pre-line">
              {connectionError}
            </p>
          </div>
          <button
            onClick={retryConnection}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Réessayer la connexion
          </button>
        </div>
      );
    }

    // Show loading state
    return (
      <div className="relative h-screen w-full p-8 bg-white">
        <Skeleton className="w-full h-full" />
        <span className="italic absolute top-1/2 right-1/2 text-gray-500">
          Connexion au serveur live...
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {isTest && <TestBanner />}
      <TopBar
        remainingQuestions={remainingQuestions}
        candidateZendeskJwt={candidateInfo?.zendeskJwt}
        candidateName={candidateName}
        candidateEmail={candidateInfo?.email}
      />
      <div className="flex flex-row gap-4 px-24 py-8 flex-grow h-0">
        <ProgressBar
          exam={exam ?? []}
          jumpToQuestion={jumpToQuestion}
          perimeter={currentlyOpenPerimeter}
          setPerimeter={(p) => {
            setPerimeter(p);
            scrollAreaRef.current?.scrollTo({
              top: scrolls[p] ?? 0,
              behavior: "instant",
            });
          }}
          displayWaiting={displayWaiting}
          setDisplayWaiting={setDisplayWaiting}
          questionsOrder={questionsOrder ?? {}}
        />
        <div className="flex flex-col gap-7 flex-grow">
          {!displayWaiting && (
            <QuestionTabs questions={questions} tab={tab} setTab={setTab} />
          )}

          <ScrollArea
            ref={scrollAreaRef}
            onScroll={(e) =>
              setScrolls((prev) => ({
                ...prev,
                [currentlyOpenPerimeter]: (e.target as HTMLElement).scrollTop,
              }))
            }
          >
            <div className="flex flex-col gap-6">
              {Object.entries(sections).map(([perimeter, sectionQuestions]) => (
                <div className="flex flex-col gap-6" key={perimeter}>
                  <div className="flex flex-row items-center gap-2 sticky top-0 bg-gray-50 z-50 border-b border-b-gray-200 py-1">
                    <h2>{Perimeter.toExamText(perimeter)}</h2>
                    <Badge variant="white">
                      {sectionQuestions.length} questions
                    </Badge>
                  </div>
                  {sectionQuestions
                    .filter(({ question }) => tabFilter(question, tab))
                    // Sort by id.
                    .sort((a, b) => a.index - b.index)
                    .map(({ question, index }) => (
                      <QuestionCard
                        name={`Question ${index + 1}`}
                        key={`${perimeter}-${index}`}
                        ref={(el) => {
                          if (el)
                            questionRefs.current[question.question.id] = el;
                        }}
                        question={question}
                        waitingQuestions={waitingQuestions}
                        setWaiting={setWaiting}
                        onAnswerAcknowledged={onAnswerAcknowledged}
                      />
                    ))}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default Questions;
